/**
 * «حساب‌های بانکی کاربران» (Phase 3 live area): every customer card/IBAN with masked value, ownership
 * state and SOURCE (provider proof vs administrative override). Never full numbers. Bank names wait for
 * the approved dataset (OPEN-13) — the UI says so instead of showing a raw code as a name. Decisions
 * are taken in the customer's Customer 360 «حساب‌های بانکی» tab (OD-38 confirmation flow).
 */
import {
  AppContainer,
  Badge,
  DataTable,
  PageHeader,
  Pagination,
  SelectField,
  StateView,
} from '@hopex/ui';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { FlaskConical } from 'lucide-react';
import { useEffect, useState } from 'react';
import { dt, type BankInstrumentPage, type BankInstrumentRow } from '../api.ts';
import { OWNERSHIP, SOURCE_LABEL } from '../lib/labels.ts';
import { Remote, useRemote } from '../lib/remote.tsx';
import { BankName } from '../lib/ui.tsx';

// `?fixture=bank-fallback` (development builds only): labelled synthetic rows for the bank-name
// fallback review. `null` in production, so the fixture module is never bundled.
const loadFixture: ((name: string) => Promise<BankInstrumentPage | null>) | null = import.meta.env
  .DEV
  ? (name) => import('../lib/bank-fixtures.ts').then((m) => m.BANK_FIXTURES[name] ?? null)
  : null;

function useBankFixture(): { active: boolean; page: BankInstrumentPage | null } {
  const search = useRouterState({ select: (s) => s.location.searchStr });
  const name = loadFixture ? new URLSearchParams(search).get('fixture') : null;
  const [loaded, setLoaded] = useState<{ name: string; page: BankInstrumentPage | null } | null>(
    null,
  );
  useEffect(() => {
    if (!name || !loadFixture) return;
    let live = true;
    void loadFixture(name).then((page) => live && setLoaded({ name, page }));
    return () => {
      live = false;
    };
  }, [name]);
  return { active: name !== null, page: loaded && loaded.name === name ? loaded.page : null };
}

export function BanksPage() {
  const fixture = useBankFixture();
  const navigate = useNavigate();
  const [ownership, setOwnership] = useState('');
  const [source, setSource] = useState('');
  const [active, setActive] = useState('');
  const [page, setPage] = useState(1);
  const p = new URLSearchParams();
  if (ownership) p.set('ownership', ownership);
  if (source) p.set('source', source);
  if (active) p.set('active', active);
  p.set('page', String(page));
  const remote = useRemote<BankInstrumentPage>(
    fixture.active ? null : `/bank-instruments?${p.toString()}`,
  );
  const r = fixture.active ? { data: fixture.page, error: null, reload: () => {} } : remote;
  const reset = (fn: (v: string) => void) => (v: string) => (fn(v), setPage(1));
  return (
    <AppContainer>
      <PageHeader title="حساب‌های بانکی کاربران" />
      {fixture.page && (
        <p className="m-0 mb-3">
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-accent-soft px-2.5 py-1 text-[11px] font-semibold text-accent-text"
            data-review-fixture
          >
            <FlaskConical aria-hidden className="size-3.5" />
            داده‌ی نمونه‌ی بازبینی — کارت یا شبای واقعی نیست
          </span>
        </p>
      )}
      <div className="mb-4 grid max-w-3xl grid-cols-3 gap-2" data-bank-filters>
        <SelectField
          label="وضعیت مالکیت"
          value={ownership}
          onChange={reset(setOwnership)}
          options={[
            { value: '', label: 'همه' },
            ...[
              'VERIFIED',
              'PENDING',
              'NO_MATCH',
              'AMBIGUOUS',
              'RECHECK_REQUIRED',
              'UNAVAILABLE',
            ].map((v) => ({ value: v, label: OWNERSHIP[v] ?? v })),
          ]}
        />
        <SelectField
          label="منبع تأیید"
          value={source}
          onChange={reset(setSource)}
          options={[
            { value: '', label: 'همه' },
            { value: 'PROVIDER', label: 'استعلام بانکی' },
            { value: 'ADMIN_OVERRIDE', label: 'تصمیم مدیر' },
          ]}
        />
        <SelectField
          label="فعال بودن"
          value={active}
          onChange={reset(setActive)}
          options={[
            { value: '', label: 'همه' },
            { value: 'YES', label: 'فعال' },
            { value: 'NO', label: 'غیرفعال' },
          ]}
        />
      </div>
      <Remote r={r}>
        {(d) => (
          <>
            <DataTable<BankInstrumentRow>
              label="کارت‌ها و شباهای کاربران"
              rows={d.items}
              density="compact"
              stickyFirst
              onRowAction={(id) => {
                const row = d.items.find((x) => x.id === id);
                if (row)
                  void navigate({
                    to: '/users/$id',
                    params: { id: row.userId },
                    search: { tab: 'banking' },
                  });
              }}
              columns={[
                {
                  id: 'masked',
                  header: 'شماره (پوشیده)',
                  isRowHeader: true,
                  cell: (b) => (
                    <span className="flex flex-col">
                      <bdi dir="ltr" className="font-mono">
                        {b.masked}
                      </bdi>
                      <span className="text-[11px] text-fg-subtle">
                        {b.kind === 'CARD' ? 'کارت' : 'شبا'}
                      </span>
                    </span>
                  ),
                },
                {
                  id: 'user',
                  header: 'کاربر',
                  cell: (b) => (
                    <Link
                      to="/users/$id"
                      params={{ id: b.userId }}
                      search={{ tab: 'banking' }}
                      className="flex flex-col hover:underline"
                    >
                      <span className="font-semibold">{b.displayName ?? 'بدون نام'}</span>
                      <bdi dir="ltr" className="font-mono text-[11px] text-fg-subtle">
                        {b.publicId}
                      </bdi>
                    </Link>
                  ),
                },
                { id: 'bank', header: 'بانک', cell: (b) => <BankName code={b.bankCode} /> },
                {
                  id: 'own',
                  header: 'وضعیت مالکیت',
                  cell: (b) => (
                    <Badge
                      tone={
                        b.ownershipStatus === 'VERIFIED'
                          ? 'positive'
                          : b.ownershipStatus === 'UNAVAILABLE' ||
                              b.ownershipStatus === 'RECHECK_REQUIRED'
                            ? 'accent'
                            : 'negative'
                      }
                    >
                      {OWNERSHIP[b.ownershipStatus] ?? 'وضعیت ثبت‌شده'}
                    </Badge>
                  ),
                },
                { id: 'src', header: 'منبع', cell: (b) => SOURCE_LABEL(b.ownershipSource) },
                { id: 'verified', header: 'تأیید', cell: (b) => dt(b.verifiedAt) },
                { id: 'active', header: 'وضعیت', cell: (b) => (b.active ? 'فعال' : 'غیرفعال') },
                { id: 'used', header: 'آخرین استفاده', cell: (b) => dt(b.lastUsedAt) },
              ]}
              empty={
                <StateView compact kind="empty" title="کارت یا شبایی با این فیلترها ثبت نشده است" />
              }
            />
            <Pagination page={d.page} pageSize={d.pageSize} total={d.total} onPage={setPage} />
          </>
        )}
      </Remote>
    </AppContainer>
  );
}
